import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, retry } from 'rxjs';
import { Timesheet, CreateTimesheetDto } from '../models/timesheet.model';
import { Project } from '../models/project.model';
import { Month } from '../models/allocation.model';
import { environment } from '../../environments/environment';

/**
 * Outcome of a manual work-log reminder. `sent` is false when the notification is switched off on
 * the Notification Settings page or the employee has no email — cases the API used to report as a
 * successful send.
 */
export interface TimesheetReminderResponse {
  sent: boolean;
  outcome: 'Sent' | 'NotificationDisabled' | 'NoRecipient' | 'Failed';
  message: string;
}

/**
 * Payload for the styled Work Log Excel export.
 *
 * The screens send the rows they are DISPLAYING; the API only renders them. Selection stays here
 * because the visible rows depend on state the server does not have — the service-group / status /
 * total-hours / PM filters, the search box, the project multi-select, and a PM's restriction to
 * their own non-shared projects. Weeks are decimal hours (7.5 = 7h 30min).
 */
export interface WorkLogExportRequest {
  periodLabel: string;
  fileNamePrefix: string;
  filters: { label: string; value: string }[];
  employees: {
    employeeName: string;
    status: string;
    weeks: number[];
    projects: { projectId: string; projectName: string; pmName: string; weeks: number[] }[];
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  private apiUrl = `${environment.apiUrl}/Timesheet`;

  constructor(private http: HttpClient) {}

  getAllTimesheets(): Observable<Timesheet[]> {
    return this.http.get<Timesheet[]>(this.apiUrl).pipe(retry(2));
  }

  getTimesheetById(id: string): Observable<Timesheet> {
    return this.http.get<Timesheet>(`${this.apiUrl}/${id}`).pipe(retry(2));
  }

  getTimesheetsByEmployee(employeeId: string): Observable<Timesheet[]> {
    return this.http.get<Timesheet[]>(`${this.apiUrl}/employee/${employeeId}`).pipe(retry(2));
  }

  getTimesheetByEmployeeMonthYear(employeeId: string, year: number, month: Month): Observable<Timesheet | null> {
    return this.http.get<Timesheet | null>(`${this.apiUrl}/employee/${employeeId}/${year}/${month}`).pipe(retry(2));
  }

  getTimesheetsByMonthYear(year: number, month: Month): Observable<Timesheet[]> {
    return this.http.get<Timesheet[]>(`${this.apiUrl}/${year}/${month}`).pipe(retry(2));
  }

  getAssignedProjects(employeeId: string, year: number, month: Month): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.apiUrl}/employee/${employeeId}/assigned-projects/${year}/${month}`).pipe(retry(2));
  }

  getPendingApprovals(): Observable<Timesheet[]> {
    return this.http.get<Timesheet[]>(`${this.apiUrl}/pending-approvals`).pipe(retry(2));
  }

  /** Renders the three-sheet Work Log workbook server-side and returns it as a download. */
  exportWorkLogsToExcel(request: WorkLogExportRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/export-excel`, request, { responseType: 'blob' });
  }

  createTimesheet(timesheet: CreateTimesheetDto): Observable<Timesheet> {
    return this.http.post<Timesheet>(this.apiUrl, timesheet);
  }

  updateTimesheet(id: string, timesheet: Timesheet): Observable<Timesheet> {
    return this.http.put<Timesheet>(`${this.apiUrl}/${id}`, timesheet);
  }

  submitTimesheet(id: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.apiUrl}/${id}/submit`, {});
  }

  revertToDraft(id: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.apiUrl}/${id}/revert-to-draft`, {});
  }

  approveTimesheet(id: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.apiUrl}/${id}/approve`, {});
  }

  // Project-wise approval APIs
  approveProject(timesheetId: string, projectId: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.apiUrl}/${timesheetId}/projects/${projectId}/approve`, {});
  }

  revertProjectApproval(timesheetId: string, projectId: string): Observable<Timesheet> {
    return this.http.post<Timesheet>(`${this.apiUrl}/${timesheetId}/projects/${projectId}/revert-approval`, {});
  }

  sendTimesheetReminder(employeeId: string, month: number, year: number): Observable<TimesheetReminderResponse> {
    return this.http.post<TimesheetReminderResponse>(
      `${this.apiUrl}/${employeeId}/send-reminder?month=${month}&year=${year}`, {});
  }

  deleteTimesheet(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}


